/**
 * Unfinished checkouts, Admin only (Trera, Oct 2026: "this pay later can't be used beyond us"). New clients who signed
 * the contract and went to pay in the last 7 days but have no booking, usually because their card was declined
 * (Loredana Tomasello: nine tries in one day). "Start booking, pay later" makes their account and booking from the
 * latest signed checkout with nothing paid; the full amount is due from their portal. The client is emailed "your date
 * is held" with a 30-day set-password link, which Admin also sees. Rules: src/lib/booking/unfinishedCheckouts.js; server: /unfinished-checkouts.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Copy, Loader2 } from 'lucide-react';
import { Button } from '@/components/admin/ui/button';
import { Input } from '@/components/admin/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/admin/ui/dialog';
import { STAGING_ROOT } from '@/lib/staging/roleNav';
import { stagingFetch, shortDate } from '../api';

const when = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/Denver' }) : '—');
const money = (n: number) => `$${Number(n || 0).toLocaleString('en-US')}`;

export default function UnfinishedCheckouts() {
  const [rows, setRows] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<any | null>(null);
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState<{ booking_id: string; set_password_link: string | null; emailed?: boolean; email_error?: string | null } | null>(null);

  const load = () => stagingFetch('/api/admin/staging/unfinished-checkouts').then((d) => { setRows(d.rows || []); setError(null); }).catch((e) => setError(e.message || String(e)));
  useEffect(() => { load(); }, []);

  const start = async () => {
    setSaving(true);
    try {
      const r = await stagingFetch(`/api/admin/staging/unfinished-checkouts/${encodeURIComponent(open.token)}/start`, { method: 'POST', body: JSON.stringify({ email, phone }) });
      setDone({ booking_id: r.booking_id, set_password_link: r.set_password_link, emailed: r.emailed, email_error: r.email_error });
      toast.success(`${open.name}'s booking started`);
      load();
    } catch (e: any) { toast.error(e.message || String(e)); } finally { setSaving(false); }
  };
  const copy = (text: string) => navigator.clipboard.writeText(text).then(() => toast.success('Link copied')).catch(() => toast.error('Could not copy; select the link instead'));

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">Admin only. New clients who signed and went to pay in the last 7 days but have no booking yet, usually a declined card. Start one only when you mean to: the client gets an account and a booking with the full amount due.</p>
      {error && <p className="text-sm text-destructive">Could not load: {error}</p>}
      {!rows && !error && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</p>}
      {rows && rows.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No unfinished checkouts in the last 7 days.</p>}
      {rows && rows.length > 0 && (
        <div className="overflow-x-auto rounded-xl border bg-card">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground"><th className="px-3 py-2">Client</th><th className="px-3 py-2">Event</th><th className="px-3 py-2">Package</th><th className="px-3 py-2">Total</th><th className="px-3 py-2">Tries</th><th className="px-3 py-2">Last try</th><th className="px-3 py-2"></th></tr></thead>
            <tbody className="divide-y">
              {rows.map((r) => (
                <tr key={r.token}>
                  <td className="px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{shortDate(r.event_date)}<span className="block text-xs text-muted-foreground">{r.location}</span></td>
                  <td className="px-3 py-2">{r.package}{r.plan !== 'full' && <span className="block text-xs text-muted-foreground">picked {r.plan}</span>}</td>
                  <td className="px-3 py-2 tabular-nums">{money(r.total)}</td>
                  <td className="px-3 py-2 tabular-nums">{r.attempts}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">{when(r.last_at)}</td>
                  <td className="px-3 py-2"><Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { setOpen(r); setEmail(''); setPhone(''); setDone(null); }}>Start booking, pay later</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={Boolean(open)} onOpenChange={(v) => { if (!v) setOpen(null); }}>
        <DialogContent>
          {open && !done && (
            <>
              <DialogHeader><DialogTitle>Start {open.name}&apos;s booking, pay later</DialogTitle><DialogDescription>{open.package} · {shortDate(open.event_date)} · {open.location}. Their account and booking are made from the contract they signed, with {money(open.total)} due from their portal (pay in full). They are emailed that their date is held, with a link to set their password.</DialogDescription></DialogHeader>
              <label className="block text-sm">Client email<Input className="mt-1" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="the email they used at checkout (in Stripe)" aria-label="Client email" /></label>
              <label className="block text-sm">Phone (optional)<Input className="mt-1" value={phone} onChange={(e) => setPhone(e.target.value)} aria-label="Phone" /></label>
              <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setOpen(null)}>Cancel</Button><Button disabled={saving || !/\S+@\S+\.\S+/.test(email)} onClick={start}>{saving ? 'Starting…' : 'Start booking'}</Button></div>
            </>
          )}
          {open && done && (
            <>
              <DialogHeader><DialogTitle>{open.name}&apos;s booking is started</DialogTitle><DialogDescription>{money(open.total)} is due from their portal. {done.emailed ? 'They were emailed this link to set their password (good for 30 days, until they first sign in).' : `The email did not go out (${done.email_error || 'unknown'}); send them this link yourself (good for 30 days).`}</DialogDescription></DialogHeader>
              {done.set_password_link ? (
                <div className="flex items-center gap-2"><Input readOnly value={done.set_password_link} onFocus={(e) => e.target.select()} aria-label="Set-password link" /><Button size="sm" variant="outline" onClick={() => copy(done.set_password_link!)}><Copy className="h-3.5 w-3.5" /></Button></div>
              ) : <p className="text-sm text-muted-foreground">No set-password link could be made; they can use Forgot password on the sign-in page.</p>}
              <div className="flex justify-end gap-2"><Link className="text-sm text-primary hover:underline" to={`${STAGING_ROOT}/bookings/${done.booking_id}`}>Open the booking →</Link></div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
